import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Loader2, UserPlus, Copy, Check, KeyRound } from "lucide-react";

const ROLES = [["pp", "PP — Paraprofessional"], ["ap", "AP — Associate Professional"], ["qp", "QP — Qualified Professional"]];

export function RoleSelect({ value, onChange, testid }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {ROLES.map(([r, label]) => (
        <button key={r} type="button" data-testid={`${testid}-${r}`} onClick={() => onChange(r)}
          className={`h-12 rounded-xl border text-sm font-semibold transition-colors ${value === r ? "bg-[#4A6B53] border-[#4A6B53] text-white" : "bg-white border-[#E2DFD8] text-[#3A453F] hover:border-[#4A6B53]"}`}>
          {r.toUpperCase()}<span className="block text-[10px] font-normal opacity-80">{label.split("— ")[1]}</span>
        </button>
      ))}
    </div>
  );
}

export function TempPasswordCard({ email, password }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(`Login: ${email}\nTemporary password: ${password}`); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };
  return (
    <div data-testid="temp-password-card" className="rounded-xl border border-[#D4A373] bg-[#FBF5EA] p-4">
      <p className="text-[10px] font-bold text-[#8a6d1f] uppercase tracking-wider flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" strokeWidth={2} /> Temporary password — share it once</p>
      <p className="text-xs text-[#3A453F] mt-1">They log in with <b>{email}</b> and will be asked to choose their own password.</p>
      <div className="mt-2 flex items-center gap-2">
        <code data-testid="temp-password-value" className="flex-1 font-mono text-lg font-bold tracking-wider text-[#121814] bg-white border border-[#E2DFD8] rounded-lg px-3 py-2">{password}</code>
        <Button data-testid="copy-temp-password-btn" variant="outline" onClick={copy} className="h-11 rounded-full border-[#E2DFD8] bg-white">
          {copied ? <Check className="w-4 h-4 mr-1.5 text-[#4A6B53]" /> : <Copy className="w-4 h-4 mr-1.5" strokeWidth={1.5} />} {copied ? "Copied" : "Copy"}
        </Button>
      </div>
    </div>
  );
}

export default function AddStaffDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", role: "pp", credentials: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = (o) => { if (!o) { setCreated(null); setForm({ name: "", email: "", role: "pp", credentials: "", password: "" }); } onOpenChange(o); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/users/staff", form);
      setCreated(data);
      toast.success(`${data.name} added as ${data.role.toUpperCase()}.`);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="add-staff-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Add a staff member</DialogTitle>
          <DialogDescription>Create their login. You can assign consumers to them right after.</DialogDescription>
        </DialogHeader>
        {created ? (
          <div className="space-y-4">
            <TempPasswordCard email={created.email} password={created.temp_password} />
            <DialogFooter>
              <Button data-testid="add-staff-done-btn" onClick={() => close(false)} className="rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Full name *</Label>
                <Input data-testid="staff-name-input" value={form.name} onChange={set("name")} className="h-12 bg-[#F9F9F6] border-[#E2DFD8]" placeholder="Jordan Lee" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Credentials</Label>
                <Input data-testid="staff-credentials-input" value={form.credentials} onChange={set("credentials")} className="h-12 bg-[#F9F9F6] border-[#E2DFD8]" placeholder="e.g. BA, QMHP" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Work email *</Label>
              <Input data-testid="staff-email-input" type="email" value={form.email} onChange={set("email")} className="h-12 bg-[#F9F9F6] border-[#E2DFD8]" placeholder="jordan@agency.org" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Role *</Label>
              <RoleSelect value={form.role} onChange={(r) => setForm((f) => ({ ...f, role: r }))} testid="staff-role" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Temporary password (optional)</Label>
              <Input data-testid="staff-password-input" value={form.password} onChange={set("password")} className="h-12 bg-[#F9F9F6] border-[#E2DFD8]" placeholder="Leave blank and I'll make one" />
            </div>
            <DialogFooter>
              <Button data-testid="create-staff-btn" onClick={save} disabled={saving || !form.name.trim() || !form.email.trim()} className="rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white h-11 px-6">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />} Create login
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
